import CryptoJS from 'crypto-js';

class RequestCryptoService {
    static tokenClaimsContext = null;

    static setTokenClaimsContext(claims = {}) {
        try {
            const context = {
                jti: claims.jti || null,
                sub: claims.sub || null,
                exp: claims.exp || null,
            };
            RequestCryptoService.tokenClaimsContext = context;
        } catch (error) {
        }
    }

    static getTokenClaimsContext() {
        try {
            if (!RequestCryptoService.tokenClaimsContext) {
                return null;
            }

            return { ...RequestCryptoService.tokenClaimsContext };
        } catch (error) {
            return null;
        }
    }

    static getClaimsFromJwt(jwtToken) {
        if (!jwtToken || jwtToken.split('.').length < 2) {
            return null;
        }

        try {
            const payload = jwtToken.split('.')[1]
                .replace(/-/g, '+')
                .replace(/_/g, '/');
            const decoded = decodeURIComponent(
                atob(payload)
                    .split('')
                    .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`)
                    .join(''),
            );
            const claims = JSON.parse(decoded);
            RequestCryptoService.setTokenClaimsContext(claims);
            return claims;
        } catch (error) {
            return null;
        }
    }

    static getJtiFromJwt(jwtToken) {
        const claims = RequestCryptoService.getClaimsFromJwt(jwtToken)
            || RequestCryptoService.getTokenClaimsContext();
        return claims && claims.jti ? claims.jti : null;
    }

    static deriveAes256Key(sourceValue) {
        return CryptoJS.SHA256(String(sourceValue || ''));
    }

    static getDecryptionKeyFromJwt(jwtToken) {
        const claims = RequestCryptoService.getClaimsFromJwt(jwtToken)
            || RequestCryptoService.getTokenClaimsContext()
            || {};
        const source = `${claims.exp || ''}:${claims.sub || ''}:${claims.jti || ''}:${claims.exp || ''}`;
        return RequestCryptoService.deriveAes256Key(source);
    }

    static getEncryptionKeyFromJwt(jwtToken) {
        const claims = RequestCryptoService.getClaimsFromJwt(jwtToken)
            || RequestCryptoService.getTokenClaimsContext()
            || {};
        const source = `${claims.sub || ''}:${claims.jti || ''}:${claims.exp || ''}:${claims.sub || ''}`;
        return RequestCryptoService.deriveAes256Key(source);
    }

    static encrypt(jwtToken, payload) {
        const serializedPayload = JSON.stringify(payload);
        const key = RequestCryptoService.getEncryptionKeyFromJwt(jwtToken);
        const iv = CryptoJS.lib.WordArray.random(16);
        const encrypted = CryptoJS.AES.encrypt(serializedPayload, key, {
            iv,
            mode: CryptoJS.mode.CBC,
            padding: CryptoJS.pad.Pkcs7,
        });

        const ivBase64 = CryptoJS.enc.Base64.stringify(iv);
        const cipherTextBase64 = CryptoJS.enc.Base64.stringify(encrypted.ciphertext);
        return `${ivBase64}.${cipherTextBase64}`;
    }

    static decrypt(jwtToken, encryptedPayload) {
        if (!encryptedPayload) {
            return encryptedPayload;
        }

        try {
            const payloadParts = String(encryptedPayload).split('.');
            const ivBase64 = payloadParts[0];
            const cipherTextBase64 = payloadParts.length > 1 ? payloadParts.slice(1).join('.') : null;
            const decryptionKey = RequestCryptoService.getDecryptionKeyFromJwt(jwtToken);

            if (!ivBase64 || !cipherTextBase64) {
                return encryptedPayload;
            }

            const iv = CryptoJS.enc.Base64.parse(ivBase64);
            const cipherParams = CryptoJS.lib.CipherParams.create({
                ciphertext: CryptoJS.enc.Base64.parse(cipherTextBase64),
            });
            const decrypted = CryptoJS.AES.decrypt(cipherParams, decryptionKey, {
                iv,
                mode: CryptoJS.mode.CBC,
                padding: CryptoJS.pad.Pkcs7,
            });
            const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);

            if (!decryptedText) {
                return encryptedPayload;
            }

            const parsed = JSON.parse(decryptedText);
            return parsed;
        } catch (error) {
            console.error('[RequestCryptoService][decrypt] error during decrypt, returning original payload', error);
            return encryptedPayload;
        }
    }
}

export default RequestCryptoService;